/* eslint-disable lingui/text-restrictions */
/* eslint-disable lingui/no-unlocalized-strings */

import { t } from "@lingui/macro";
import { ArrowRight, Robot, Star, User } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useAuthStore } from "@/client/stores/auth";

const quickActions = [
  { label: "Review My Resume", desc: "Full 0-100 score + actionable suggestions" },
  { label: "Write My Summary", desc: "Professional summary from your full resume" },
  { label: "Suggest Improvements", desc: "Section-by-section enhancement ideas" },
  { label: "Tailor to Job", desc: "Paste a JD and get specific tailoring steps" },
];

const chatMessages = [
  {
    role: "user" as const,
    text: "Review my resume and tell me what to improve",
  },
  {
    role: "assistant" as const,
    text: "I've analyzed your resume against 2026 hiring standards. Your overall score is 87/100. Strengths: strong action verbs, good quantification. Areas to improve: your summary could be more targeted, and your skills section is missing 3 keywords common in Senior Engineer roles...",
  },
];

export const FeatureAIAssistantSection = () => {
  const isLoggedIn = useAuthStore((s) => !!s.user);

  return (
    <section id="ai-assistant" className="relative py-24 sm:py-32">
      <div className="container">
        <div className="mx-auto grid max-w-6xl items-start gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: content + chat mockup */}
          <motion.div
            className="space-y-6"
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              {t`AI Assistant`}
            </p>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              {t`Your Personal AI Career Assistant`}
            </h2>
            <p className="text-lg leading-relaxed text-foreground/60">
              Not sure what to improve? Ask. CareerBldr's AI assistant lives inside your builder,
              knows your entire resume, and gives expert advice in a conversational chat. Get a full
              review, write your summary, or tailor to a specific job. Just ask.
            </p>

            {/* Chat mockup */}
            <div className="space-y-3 rounded-xl border border-border/50 bg-background p-5">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Robot size={14} className="text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary-accent/80 text-foreground/70"
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.role === "user" && (
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/10">
                      <User size={14} className="text-foreground/60" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {quickActions.map((action) => (
                <div
                  key={action.label}
                  className="rounded-lg border border-border/50 px-3.5 py-3 transition-colors hover:border-border"
                >
                  <p className="text-xs font-semibold">{action.label}</p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-foreground/45">
                    {action.desc}
                  </p>
                </div>
              ))}
            </div>

            <Button asChild size="lg">
              <Link to={isLoggedIn ? "/dashboard" : "/auth/register"}>
                {isLoggedIn ? t`Open AI Assistant` : t`Try AI Assistant`}
                <ArrowRight className="ml-2" weight="bold" />
              </Link>
            </Button>
          </motion.div>

          {/* Right: screenshot */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0, transition: { duration: 0.6, delay: 0.15 } }}
            className="lg:sticky lg:top-24"
          >
            <div className="relative">
              <img
                src="/screenshots/ai-assistant.png"
                alt="AI Career Assistant showing a 98/100 resume review score with actionable improvement suggestions"
                loading="lazy"
                decoding="async"
                className="w-full rounded-xl shadow-2xl ring-1 ring-foreground/10"
              />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-center rounded-b-xl bg-gradient-to-t from-black/60 via-black/30 to-transparent pb-4 pt-12">
                <div className="flex flex-col items-center gap-1 text-white/80">
                  <span className="text-xs font-medium">3 more suggestions below</span>
                  <svg className="h-4 w-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Social proof snippet */}
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0, transition: { delay: 0.4 } }}
              className="mt-4 rounded-xl border border-border/50 bg-background p-4"
            >
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} weight="fill" className="h-3.5 w-3.5 text-yellow-500" />
                ))}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground/60">
                "The AI assistant is like having a career coach on demand. I asked it to review my
                resume and got a score with specific fixes. Three interviews that week."
              </p>
              <p className="mt-2 text-xs font-medium text-foreground/40">— K. Lietzau, Product Designer</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
